"use client";

import React, { useCallback, useMemo, memo } from "react";
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Spinner,
  createToaster,
  type BoxProps,
} from "@chakra-ui/react";
import { FullViewResume } from "../../../_models/resume";
import {
  A4_WIDTH_PX,
  A4_HEIGHT_PX,
  A4_MARGIN_PX,
  A4_PDF_VIEWER_HEIGHT_PX,
} from "./utils/a4Constants";
import {
  ResumeHeader,
  ExperienceSection,
  EducationSection,
  CertificatesSection,
  SkillsSection,
  LanguagesSection,
} from "./preview";
import { downloadAtsResumePdf } from "./ATSResumePdfExport";
import { AtsResumePdfViewer } from "./AtsResumePdfViewer";

export interface ATSResumeProps {
  data: FullViewResume;
  showExportButton?: boolean;
  exportButtonText?: string;
  onExportStart?: () => void;
  onExportSuccess?: (filename: string) => void;
  onExportError?: (error: Error) => void;
  /**
   * `pdf` — same render as download (embedded PDFViewer, like react-pdf REPL). Recommended.
   * `html` — legacy Chakra layout (differs from exported PDF).
   */
  previewMode?: "pdf" | "html";
  /** Height of embedded PDF viewer when `previewMode="pdf"` */
  pdfPreviewHeight?: number | string;
  /** Toolbar on embedded viewer (zoom, etc.) */
  pdfShowToolbar?: boolean;
}

const toaster = createToaster({
  placement: "top",
});

const exportToPDF = async (
  data: FullViewResume,
  filename: string,
  setExporting: (loading: boolean) => void,
  onExportStart?: () => void,
  onExportSuccess?: (filename: string) => void,
  onExportError?: (error: Error) => void,
) => {
  try {
    setExporting(true);
    onExportStart?.();

    await downloadAtsResumePdf(data, filename);

    onExportSuccess?.(filename);
    toaster.create({
      title: "Export Successful",
      description: `Resume exported as ${filename}`,
      type: "success",
      duration: 4000,
    });
  } catch (error) {
    const exportError =
      error instanceof Error ? error : new Error("Unknown export error");

    onExportError?.(exportError);
    toaster.create({
      title: "Export Failed",
      description: exportError.message,
      type: "error",
      duration: 4000,
    });
  } finally {
    setExporting(false);
  }
};

interface ATSResumeRootProps extends BoxProps, ATSResumeProps {}

const ATSResumeRoot = memo<ATSResumeRootProps>(
  ({
    data,
    showExportButton = true,
    exportButtonText = "Export to PDF",
    onExportStart,
    onExportSuccess,
    onExportError,
    previewMode = "pdf",
    pdfPreviewHeight = A4_PDF_VIEWER_HEIGHT_PX,
    pdfShowToolbar = true,
    ...boxProps
  }) => {
    const [isExporting, setIsExporting] = React.useState(false);

    const htmlSections = useMemo(
      () =>
        [
          <ResumeHeader
            key="header"
            basicInfo={data.resume.job_details.basic_info}
          />,
          data.resume.job_details.experiences?.length > 0 && (
            <ExperienceSection
              key="experience"
              experiences={data.resume.job_details.experiences}
            />
          ),
          data.resume.job_details.educations?.length > 0 && (
            <EducationSection
              key="education"
              educations={data.resume.job_details.educations}
            />
          ),
          data.resume.job_details.certificates &&
            data.resume.job_details.certificates.length > 0 && (
              <CertificatesSection
                key="certificates"
                certificates={data.resume.job_details.certificates}
              />
            ),
          data.resume.job_details.skills?.length > 0 && (
            <SkillsSection
              key="skills"
              skills={data.resume.job_details.skills}
            />
          ),
          data.resume.job_details.professional_languages?.length > 0 && (
            <LanguagesSection
              key="languages"
              languages={data.resume.job_details.professional_languages}
            />
          ),
        ].filter(Boolean),
      [data],
    );

    const filename = useMemo(
      () =>
        `${data.resume.job_details.basic_info.name.replace(/\s+/g, "_")}_Resume.pdf`,
      [data.resume.job_details.basic_info.name],
    );

    const handleExport = useCallback(() => {
      exportToPDF(
        data,
        filename,
        setIsExporting,
        onExportStart,
        onExportSuccess,
        onExportError,
      );
    }, [data, filename, onExportStart, onExportSuccess, onExportError]);

    return (
      <Box p={4} minHeight="100vh" {...boxProps} id="resume-content-container">
        {showExportButton && (
          <Box mb={4} textAlign="center">
            <HStack gap={4} justify="center">
              <Button
                colorScheme="blue"
                size="md"
                loading={isExporting}
                onClick={handleExport}
                disabled={isExporting}
                aria-label="Export resume to PDF"
              >
                {isExporting ? (
                  <HStack gap={2}>
                    <Spinner size="sm" />
                    <Text>Exporting...</Text>
                  </HStack>
                ) : (
                  exportButtonText
                )}
              </Button>
            </HStack>
          </Box>
        )}
        <Box
          id="resume-content"
          data-testid="resume-content"
          as="article"
          aria-label="Resume"
          py={4}
          mt={4}
        >
          {previewMode === "pdf" ? (
            <AtsResumePdfViewer
              jobDetails={data.resume.job_details}
              height={pdfPreviewHeight}
              showToolbar={pdfShowToolbar}
            />
          ) : (
            <Box
              width={`${A4_WIDTH_PX}px`}
              minHeight={`${A4_HEIGHT_PX}px`}
              maxWidth="100%"
              mx="auto"
              bg="white"
              p={`${A4_MARGIN_PX}px`}
              fontFamily="system-ui, -apple-system, 'Inter', sans-serif"
              lineHeight="1.5"
              boxSizing="border-box"
              boxShadow="lg"
            >
              <VStack align="stretch" gap={0}>
                {htmlSections}
              </VStack>
            </Box>
          )}
        </Box>
      </Box>
    );
  },
);
ATSResumeRoot.displayName = "ATSResumeRoot";

export const ATSResume = Object.assign(ATSResumeRoot, {
  Root: ATSResumeRoot,
  Header: ResumeHeader,
  Experience: ExperienceSection,
  Education: EducationSection,
  Skills: SkillsSection,
  Certificates: CertificatesSection,
  Languages: LanguagesSection,
});
